import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  Alert,
  CircularProgress,
  Chip,
  Paper,
  Tooltip,
  TextField,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  VideoCall,
  VideocamOff,
  Mic,
  MicOff,
  CallEnd,
  Chat,
  Settings,
  FullscreenExit,
  Fullscreen,
  Send,
} from '@mui/icons-material';
import {
  DefaultMeetingSession,
  DefaultDeviceController,
  ConsoleLogger,
  LogLevel,
  MeetingSessionConfiguration,
  AudioVideoFacade,
  VideoTileState,
  DeviceChangeObserver,
  AudioVideoObserver,
  MeetingSessionStatus,
  MeetingSessionStatusCode,
} from 'amazon-chime-sdk-js';
import { appointmentAPI, MeetingResponse, Appointment } from '../services/apiService';

interface VideoConsultationProps {
  appointment: Appointment;
  open: boolean;
  onClose: () => void;
  userType: 'patient' | 'provider';
}

interface DeviceInfo {
  deviceId: string;
  label: string;
}

const VideoConsultation: React.FC<VideoConsultationProps> = ({
  appointment,
  open,
  onClose,
  userType,
}) => {
  // Meeting state
  const [meetingSession, setMeetingSession] = useState<DefaultMeetingSession | null>(null);
  const [isJoining, setIsJoining] = useState(false);
  const [isJoined, setIsJoined] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [meetingEnded, setMeetingEnded] = useState(false);

  // Controls state
  const [isVideoEnabled, setIsVideoEnabled] = useState(false);
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Chat state
  const [chatMessages, setChatMessages] = useState<Array<{id: string; sender: string; message: string; timestamp: string}>>([]);
  const [newMessage, setNewMessage] = useState('');

  // Device state
  const [audioDevices, setAudioDevices] = useState<DeviceInfo[]>([]);
  const [videoDevices, setVideoDevices] = useState<DeviceInfo[]>([]);
  const [selectedAudioDevice, setSelectedAudioDevice] = useState<string>('');
  const [selectedVideoDevice, setSelectedVideoDevice] = useState<string>('');

  // Video elements
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);

  // Meeting configuration
  const logger = new ConsoleLogger('VideoConsultation', LogLevel.INFO);
  const deviceController = new DefaultDeviceController(logger);

  // Device change observer
  const deviceChangeObserver: DeviceChangeObserver = {
    audioInputsChanged: (audioInputDevices: MediaDeviceInfo[]) => {
      setAudioDevices(audioInputDevices.map(d => ({ deviceId: d.deviceId, label: d.label })));
    },
    videoInputsChanged: (videoInputDevices: MediaDeviceInfo[]) => {
      setVideoDevices(videoInputDevices.map(d => ({ deviceId: d.deviceId, label: d.label })));
    },
  };

  // Meeting observer  
  const meetingObserver: AudioVideoObserver = {
    audioVideoDidStart: () => {
      setIsJoined(true);
      setIsJoining(false);
      updatePresence('joined');
    },
    audioVideoDidStop: (sessionStatus: MeetingSessionStatus) => {
      setIsJoined(false);
      setMeetingEnded(true);
      
      if (sessionStatus.statusCode() !== MeetingSessionStatusCode.OK) {
        setError('Meeting ended unexpectedly');
      }
      updatePresence('left');
    },
    videoTileDidUpdate: (tileState: VideoTileState) => {
      if (!tileState.boundVideoElement) {
        if (tileState.localTile) {
          meetingSession?.audioVideo.bindVideoElement(tileState.tileId, localVideoRef.current!);
        } else {
          meetingSession?.audioVideo.bindVideoElement(tileState.tileId, remoteVideoRef.current!);
        }
      }
    },
    videoTileWasRemoved: (tileId: number) => {
    },
  };

  // Initialize devices
  useEffect(() => {
    if (open) {
      initializeDevices();
    }
    return () => {
      deviceController.removeDeviceChangeObserver(deviceChangeObserver);
    };
  }, [open]);

  const initializeDevices = async () => {
    try {
      deviceController.addDeviceChangeObserver(deviceChangeObserver);
      
      const audioDevices = await deviceController.listAudioInputDevices();
      const videoDevices = await deviceController.listVideoInputDevices();
      
      setAudioDevices(audioDevices.map(d => ({ deviceId: d.deviceId, label: d.label })));
      setVideoDevices(videoDevices.map(d => ({ deviceId: d.deviceId, label: d.label })));
      
      if (audioDevices.length > 0) {
        setSelectedAudioDevice(audioDevices[0].deviceId);
      }
      if (videoDevices.length > 0) {
        setSelectedVideoDevice(videoDevices[0].deviceId);
      }
    } catch (err) {
      setError('Failed to initialize audio/video devices');
    }
  };

  const updatePresence = async (action: 'joined' | 'left') => {
    console.log(`Presence update: ${userType} ${action} appointment ${appointment.appointmentId}`);
  };

  const joinMeeting = async () => {
    if (isJoining || isJoined) return;
    
    setIsJoining(true);
    setError(null);
    
    try {
      let meetingResponse: MeetingResponse;
      
      // Create or get meeting info
      if (!appointment.meetingId) {
        meetingResponse = await appointmentAPI.createMeeting(appointment.appointmentId);
      } else {
        meetingResponse = await appointmentAPI.getMeetingInfo(appointment.appointmentId);
      }
      
      // Create attendee
      const attendeeResponse = await appointmentAPI.createAttendee(appointment.appointmentId, { userType });
      
      if (!attendeeResponse.meeting || !attendeeResponse.attendee) {
        throw new Error('Invalid meeting or attendee response');
      }
      
      // Create meeting configuration
      const configuration = new MeetingSessionConfiguration(
        attendeeResponse.meeting,
        attendeeResponse.attendee
      );
      
      // Create meeting session
      const session = new DefaultMeetingSession(
        configuration,
        logger,
        deviceController
      );
      
      setMeetingSession(session);
      
      // Add observers
      session.audioVideo.addObserver(meetingObserver);
      
      // Set up audio
      if (selectedAudioDevice && audioRef.current) {
        await session.audioVideo.chooseAudioInputDevice(selectedAudioDevice);
        session.audioVideo.bindAudioElement(audioRef.current);
      }
      
      // Set up video preview
      if (selectedVideoDevice && isVideoEnabled && localVideoRef.current) {
        await session.audioVideo.chooseVideoInputDevice(selectedVideoDevice);
        session.audioVideo.startLocalVideoTile();
      }
      
      // Start the meeting
      session.audioVideo.start();
      
    } catch (err) {
      setError('Failed to join video consultation. Please try again.');
      setIsJoining(false);
    }
  };

  const leaveMeeting = () => {
    if (meetingSession) {
      meetingSession.audioVideo.stop();
      setMeetingSession(null);
    }
    setIsJoined(false);
    setMeetingEnded(true);
    onClose();
  };

  const toggleVideo = useCallback(async () => {
    if (!meetingSession) return;
    
    if (isVideoEnabled) {
      meetingSession.audioVideo.stopLocalVideoTile();
      setIsVideoEnabled(false);
    } else {
      if (selectedVideoDevice) {
        await meetingSession.audioVideo.chooseVideoInputDevice(selectedVideoDevice);
        meetingSession.audioVideo.startLocalVideoTile();
        setIsVideoEnabled(true);
      }
    }
  }, [meetingSession, isVideoEnabled, selectedVideoDevice]);

  const toggleAudio = useCallback(async () => {
    if (!meetingSession) return;
    
    if (isAudioEnabled) {
      meetingSession.audioVideo.realtimeMuteLocalAudio();
      setIsAudioEnabled(false);
    } else {
      meetingSession.audioVideo.realtimeUnmuteLocalAudio();
      setIsAudioEnabled(true);
    }
  }, [meetingSession, isAudioEnabled]);

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const sendMessage = () => {
    if (newMessage.trim()) {
      const message = {
        id: Date.now().toString(),
        sender: userType === 'patient' ? 'Patient' : 'Provider',
        message: newMessage.trim(),
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };
      setChatMessages(prev => [...prev, message]);
      setNewMessage('');
      
      // In production, send to other participant via WebSocket or API
      // For now, just add to local state
    }
  };

  const formatDateTime = (dateTime: string) => {
    const date = new Date(dateTime);
    return {
      date: date.toLocaleDateString(),
      time: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    };
  };

  const { date, time } = formatDateTime(appointment.dateTime);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={isFullscreen ? false : 'lg'}
      fullWidth
      fullScreen={isFullscreen}
      PaperProps={{
        sx: {
          height: isFullscreen ? '100vh' : '80vh',
          maxHeight: isFullscreen ? '100vh' : '80vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
        <Typography variant="h6">
          Video Consultation - {date} at {time}
        </Typography>
        <Box>
          <Tooltip title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}>
            <IconButton onClick={toggleFullscreen}>
              {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ p: 2, height: '100%' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {meetingEnded && (
          <Alert severity="info" sx={{ mb: 2 }}>
            The video consultation has ended.
          </Alert>
        )}

        {!isJoined && !meetingEnded ? (
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <VideoCall sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                Ready to join your consultation?
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Provider: {appointment.providerEmail}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                Make sure your camera and microphone are working properly
              </Typography>
              
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 3 }}>
                <Chip
                  label={`Camera: ${isVideoEnabled ? 'On' : 'Off'}`}
                  color={isVideoEnabled ? 'success' : 'default'}
                />
                <Chip
                  label={`Microphone: ${isAudioEnabled ? 'On' : 'Off'}`}
                  color={isAudioEnabled ? 'success' : 'default'}
                />
              </Box>

              <Button
                variant="contained"
                size="large"
                startIcon={<VideoCall />}
                onClick={joinMeeting}
                disabled={isJoining}
                sx={{ minWidth: 200 }}
              >
                {isJoining ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Joining...
                  </>
                ) : (
                  'Join Consultation'
                )}
              </Button>
            </CardContent>
          </Card>
        ) : isJoined ? (
          <Grid container spacing={2} sx={{ height: '100%' }}>
            <Grid item xs={12} md={8}>
              <Paper
                sx={{
                  position: 'relative',
                  height: '100%',
                  minHeight: 400,
                  backgroundColor: 'black',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <video
                  ref={remoteVideoRef}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                  autoPlay
                  playsInline
                />
                
                {/* Local video overlay */}
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 16,
                    right: 16,
                    width: 160,
                    height: 120,
                    backgroundColor: 'black',
                    borderRadius: 1,
                    overflow: 'hidden',
                  }}
                >
                  <video
                    ref={localVideoRef}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                    autoPlay
                    playsInline
                    muted
                  />
                </Box>

                {/* Audio element (hidden) */}
                <audio ref={audioRef} autoPlay />
              </Paper>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" gutterBottom>
                    Consultation Info
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Date:</strong> {date}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Time:</strong> {time}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Provider:</strong> {appointment.providerEmail}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    <strong>Status:</strong> {appointment.status.toUpperCase()}
                  </Typography>

                  {/* Chat */}
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    <Chat sx={{ mr: 1, fontSize: 16 }} />
                    Consultation Chat
                  </Typography>
                  <Paper
                    sx={{
                      height: 200,
                      display: 'flex',
                      flexDirection: 'column',
                      backgroundColor: 'grey.50',
                    }}
                  >
                    {/* Messages */}
                    <Box sx={{ flexGrow: 1, overflow: 'auto', p: 1 }}>
                      <List dense>
                        {chatMessages.length === 0 ? (
                          <ListItem>
                            <ListItemText 
                              primary="Welcome to your FMLA consultation"
                              secondary="You can send messages here during your video call"
                              sx={{ textAlign: 'center', color: 'text.secondary' }}
                            />
                          </ListItem>
                        ) : (
                          chatMessages.map((msg) => (
                            <ListItem key={msg.id} sx={{ py: 0.5 }}>
                              <ListItemText
                                primary={
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="caption" color="primary">
                                      {msg.sender}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {msg.timestamp}
                                    </Typography>
                                  </Box>
                                }
                                secondary={msg.message}
                              />
                            </ListItem>
                          ))
                        )}
                      </List>
                    </Box>
                    {/* Message input */}
                    <Box sx={{ p: 1, borderTop: 1, borderColor: 'divider' }}>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                          size="small"
                          fullWidth
                          placeholder="Type a message..."
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                        />
                        <IconButton 
                          size="small" 
                          onClick={sendMessage}
                          disabled={!newMessage.trim()}
                          color="primary"
                        >
                          <Send />
                        </IconButton>
                      </Box>
                    </Box>
                  </Paper>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        ) : null}

        {isJoined && (
          <Box
            sx={{
              position: 'fixed',
              bottom: 24,
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              gap: 1,
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              borderRadius: 4,
              p: 1,
              zIndex: 1000,
            }}
          >
            <IconButton
              onClick={toggleVideo}
              sx={{
                color: isVideoEnabled ? 'white' : 'error.main',
                backgroundColor: isVideoEnabled ? 'rgba(255, 255, 255, 0.2)' : 'rgba(244, 67, 54, 0.2)',
              }}
            >
              {isVideoEnabled ? <VideoCall /> : <VideocamOff />}
            </IconButton>
            
            <IconButton
              onClick={toggleAudio}
              sx={{
                color: isAudioEnabled ? 'white' : 'error.main',
                backgroundColor: isAudioEnabled ? 'rgba(255, 255, 255, 0.2)' : 'rgba(244, 67, 54, 0.2)',
              }}
            >
              {isAudioEnabled ? <Mic /> : <MicOff />}
            </IconButton>

            <IconButton
              onClick={leaveMeeting}
              sx={{
                color: 'white',
                backgroundColor: 'error.main',
              }}
            >
              <CallEnd />
            </IconButton>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default VideoConsultation;